import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/react-hooks";

import { Modal, Text, Input, InputContainer, Button } from "components/common";
import CheckmarkIcon from "assets/CheckmarkIcon";

import {
  SUCCESS_STATUS,
  ERROR_MESSAGE,
  NO_PRN_GALILEO_STATUS,
  GALILEO_WARNING
} from "./consts";
import { cleanNameInput } from "./utils";
import UPDATE_USER_NAMES from "./gql-statements";

/**
 * @param {user: {firstName: String}} user Our portalUser query for user information
 * passed down from SupportalUserView. See [QUERY.js]("../../../Pages/CustomerSupport/query.js")
 */
const UpdateUserNames = ({ user }) => {
  const [isModalShowing, setIsModalShowing] = useState(false);
  const [error, setError] = useState(null);
  const [galileoWarning, setGalileoWarning] = useState(null);
  const [wasUpdateSuccessful, setWasUpdateSuccessful] = useState(false);
  const [newFirstName, setNewFirstName] = useState(user?.first_name || "");
  const [newLastName, setNewLastName] = useState(user?.last_name || "");
  const [updateUserNames, { loading: updateUserNamesInFlight }] = useMutation(
    UPDATE_USER_NAMES
  );

  useEffect(() => {
    if (!isModalShowing) {
      setNewFirstName(user?.first_name);
      setNewLastName(user?.last_name);
      setError(null);
      setWasUpdateSuccessful(false);
    }
  }, [isModalShowing]);

  const handleUpdateName = async () => {
    const variables = {
      email: user.email,
      first_name: newFirstName,
      last_name: newLastName
    };
    try {
      const res = await updateUserNames({ variables });
      if (res.data?.updateName?.status === SUCCESS_STATUS) {
        setWasUpdateSuccessful(true);
      } else {
        console.log(res.data, "data");
        setError(ERROR_MESSAGE);
      }
    } catch (err) {
      if (err.message === NO_PRN_GALILEO_STATUS) {
        setWasUpdateSuccessful(true);
        setGalileoWarning(GALILEO_WARNING);
      }
    }
  };

  if (!isModalShowing) {
    return (
      <button
        className="btn-green text-white px-3 py-1 "
        type="button"
        onClick={() => setIsModalShowing(true)}
      >
        edit name
      </button>
    );
  } else if (isModalShowing) {
    return (
      <div className="bg-black" style={{ maxWidth: 500 }}>
        <Modal
          isOpen={isModalShowing}
          close={() => setIsModalShowing(false)}
          hasCrossContainer={false}
        >
          <div className="bg-white">
            <div className="bg-black text-white text-xl py-8 px-5 font-extralight">
              <p>Update name</p>
            </div>
            {wasUpdateSuccessful ? (
              <div className="mx-4 pt-4">
                <div className="flex justify-center">
                  <CheckmarkIcon />
                  <span className="text-green-500 pl-1">
                    User Information Updated
                  </span>
                </div>
                {galileoWarning && (
                  <p className="text-red-500 font-extralight mx-auto py-2">
                    <i className="fas fa-exclamation-circle mr-1" />
                    {galileoWarning}
                  </p>
                )}
                <Button
                  onClick={() => window.location.reload()}
                  className="my-5 py-5 text-sm rounded-none"
                >
                  Refresh
                </Button>
              </div>
            ) : (
              <>
                <div className="flex justify-start flex-col mx-5">
                  <Text data-testid="fn-input" className="text-base mt-3 mb-1">
                    First Name*
                  </Text>
                  <InputContainer className="rounded-none">
                    <Input
                      data-testid="UpdateUserNamesFirst"
                      value={newFirstName}
                      onChange={(e) =>
                        cleanNameInput(e.target.value, setNewFirstName)
                      }
                      pattern="[a-zA-Z]"
                    />
                  </InputContainer>
                  <Text className="text-base mt-3 mb-1">Last Name*</Text>
                  <InputContainer className="rounded-none">
                    <Input
                      data-testid="UpdateUserNamesLast"
                      value={newLastName}
                      onChange={(e) =>
                        cleanNameInput(e.target.value, setNewLastName)
                      }
                      pattern="[a-zA-Z]"
                    />
                  </InputContainer>
                  {error && (
                    <p className="text-red-500 font-extralight mx-auto py-2">
                      <i className="fas fa-exclamation-circle mr-1" />
                      {error}
                    </p>
                  )}
                </div>
                <Button
                  onClick={handleUpdateName}
                  disabled={
                    !newLastName || !newFirstName || updateUserNamesInFlight
                  }
                  className="my-5 py-5 mx-5 text-sm rounded-none"
                >
                  Update
                </Button>
              </>
            )}
          </div>
        </Modal>
      </div>
    );
  }
};

export default UpdateUserNames;
